import { addDays } from "_utils/dateUtils";
import { useCallback, useMemo } from "react";
import { useCookies as useReactCookies } from "react-cookie";

const cookieOptions = {
  secure: true,
  sameSite: true,
  expires: addDays(new Date(), 100),
};

const detectBoolProperties = (obj: unknown) => {
  return Object.entries(obj)
    .filter((pair) => ["true", "false"].includes(pair[1]))
    .map((pair) => pair[0]);
};

const parseBoolProperties = (obj: unknown) => {
  const boolProperties = detectBoolProperties(obj);
  return boolProperties.reduce((acc, key) => ({ ...acc, [key]: obj[key] === "true" }), {});
};

export const useCookies = <T,>() => {
  const [cookies, setCookie, removeCookie] = useReactCookies();

  const parsedBoolObj = useCallback(() => parseBoolProperties(cookies), [cookies]);

  const typedCookies = useMemo(() => {
    return {
      ...cookies,
      ...parsedBoolObj,
    } as T;
  }, [cookies, parsedBoolObj]);

  const updateCookie = useCallback(
    (key: string, value: unknown) => {
      if (!!value) {
        setCookie(key, value, cookieOptions);
      } else {
        removeCookie(key, cookieOptions);
      }
    },
    [removeCookie, setCookie],
  );

  const storeCookies = useCallback(
    (cookies: T) => {
      Object.entries(cookies).forEach(([key, value]: [string, any]) => updateCookie(key, value));
    },
    [updateCookie],
  );

  return { cookies: typedCookies, storeCookies };
};
